import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { QUERY_MUNICIPALITIES_WITH_SEARCH } from '@/graphql/queries/municipalities';
import { MunicipalityWithSearch } from '@/graphql/types/municipality';
import { AutocompleteStyled, Option } from '../autocomplete';
import { useTranslation } from '@/hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';
import { Box, IconButton, Tooltip } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';

export interface MunicipalityAutocompleteProps {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  value?: MunicipalityWithSearch | null;
  onChange: (event: React.SyntheticEvent, value: MunicipalityWithSearch | null) => void;
  disabled?: boolean;
  required?: boolean;
  margin?: 'normal' | 'dense' | 'none';
  fullWidth?: boolean;
  helperText?: string;
  initialLimit?: number;
  className?: string;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export const MunicipalityAutocomplete = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  required = false,
  margin = 'normal',
  fullWidth = true,
  helperText,
  initialLimit = 30,
  className,
  ref,
}: MunicipalityAutocompleteProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const existData = useMemo(() => {
    if (value) {
      return true;
    }
    return false;
  }, [value]);

  // Query para buscar municípios
  const { data, loading } = useQuery(QUERY_MUNICIPALITIES_WITH_SEARCH, {
    variables: {
      search: existData ? undefined : searchTerm,
      page: 1,
      limit: initialLimit,
    },
    skip: existData || searchTerm.length < 3, // Só executa se já existe dado ou se o termo de busca tem 3+ caracteres
  });

  // Atualiza as opções quando os dados chegam
  useEffect(() => {
    if (data?.getAllMunicipalitiesWithSearch?.data) {
      const municipalityOptions: Option[] = data.getAllMunicipalitiesWithSearch.data.map(
        (municipality: MunicipalityWithSearch) => ({
          label: municipality.display_name || municipality.name_municipality,
          value: municipality.municipality_code,
          // Armazena o objeto completo do município para uso posterior
          municipalityData: municipality,
        })
      );
      setOptions(municipalityOptions);
      setOpen(municipalityOptions.length > 0);
    } else if (searchTerm.length > 0 && searchTerm.length < 3) {
      // Limpa as opções se o usuário digitou menos de 3 caracteres
      setOptions([]);
      setOpen(false);
    }
  }, [data, searchTerm]);

  // Atualiza o valor do input quando o value prop muda
  useEffect(() => {
    if (value) {
      setInputValue(value.display_name || value.name_municipality);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Cleanup do timeout quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Função para lidar com mudanças no input de busca
  const handleInputChange = useCallback((_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);

    // Limpa o timeout anterior
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce - atualiza o termo de busca após um pequeno delay
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchTerm(newInputValue);
    }, 300);
  }, []);

  // Função para lidar com mudanças na seleção
  const handleChange = useCallback(
    (event: React.SyntheticEvent, selectedOption: Option | null) => {
      if (selectedOption && selectedOption.municipalityData) {
        onChange(event, selectedOption.municipalityData);
        setOpen(false);
      } else {
        onChange(event, null);
      }
    },
    [onChange]
  );

  const handleOpenMunicipalityPage = useCallback(
    (event: React.MouseEvent, municipalityData: MunicipalityWithSearch) => {
      event.preventDefault();
      event.stopPropagation();
      navigate(`${AppRouters.municipality}/${municipalityData.municipality_code}`, {
        state: {
          name_municipality: municipalityData.name_municipality,
        },
      });
    },
    [navigate]
  );

  const stopOptionSelection = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Função para determinar se uma opção é igual ao valor atual
  const isOptionEqualToValue = useCallback((option: Option, currentValue: Option) => {
    return option.value === currentValue.value;
  }, []);

  // Função para obter o label da opção
  const getOptionLabel = useCallback((option: Option) => {
    return option.label;
  }, []);

  // Encontra a opção atual baseada no value prop
  const currentOption = value
    ? options.find((option) => option.value === value.municipality_code) || {
        label: value.display_name || value.name_municipality,
        value: value.municipality_code,
        municipalityData: value,
      }
    : null;

  return (
    <AutocompleteStyled
      ref={ref}
      className={className}
      id={id}
      name={name}
      label={label}
      placeholder={placeholder || t('placeholder.placeholder.search.municipality')}
      value={currentOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      open={open}
      onOpen={() => {
        if (options.length > 0) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      loading={loading}
      disabled={disabled}
      required={required}
      margin={margin}
      fullWidth={fullWidth}
      helperText={helperText}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.label}
            </Box>
            {option.municipalityData && (
              <Tooltip title={t('button.button.see_more')}>
                <IconButton
                  aria-label={t('button.button.see_more')}
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  onMouseDown={stopOptionSelection}
                  onTouchStart={stopOptionSelection}
                  sx={(theme) => ({
                    flexShrink: 0,
                    marginLeft: 'auto',
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette['mint-green'][800],
                    '&:hover': {
                      backgroundColor: theme.palette['mint-green'][950],
                    },
                  })}
                  onClick={(event) =>
                    handleOpenMunicipalityPage(
                      event,
                      option.municipalityData as MunicipalityWithSearch
                    )
                  }
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }}
    />
  );
};

// Extensão da interface Option para incluir dados do município
declare module '../autocomplete' {
  interface Option {
    municipalityData?: MunicipalityWithSearch;
  }
}
